import { useState, useEffect } from 'react'
import { Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useProject, useUpdateProject } from '@/hooks/useProjects'
import { toast } from 'sonner'
import { useAnalytics } from '@/hooks/useAnalytics'

interface VfxSettingsManagerProps {
  projectId: string
  canEdit?: boolean
}

const MARKER_COLORS = [
  { value: 'RED', label: 'Red', hex: '#ff0000' },
  { value: 'YELLOW', label: 'Yellow', hex: '#ffff00' },
  { value: 'GREEN', label: 'Green', hex: '#00ff00' },
  { value: 'CYAN', label: 'Cyan', hex: '#00ffff' },
  { value: 'BLUE', label: 'Blue', hex: '#0000ff' },
  { value: 'MAGENTA', label: 'Magenta', hex: '#ff00ff' },
  { value: 'PINK', label: 'Pink', hex: '#ff69b4' },
  { value: 'ORANGE', label: 'Orange', hex: '#ff8800' },
  { value: 'PURPLE', label: 'Purple', hex: '#8800ff' },
]

/**
 * VfxSettingsManager - Configure VFX shot detection settings
 *
 * Features:
 * - Select marker color for VFX shot detection
 * - Settings stored in project.custom_fields.vfx_settings
 */
export function VfxSettingsManager({ projectId, canEdit = true }: VfxSettingsManagerProps) {
  const { data: project, isLoading } = useProject(projectId)
  const updateProjectMutation = useUpdateProject()
  const { track } = useAnalytics()

  const [selectedColor, setSelectedColor] = useState<string>('RED')
  const [hasChanges, setHasChanges] = useState(false)

  // Load settings from project when it loads
  useEffect(() => {
    if (project?.custom_fields?.vfx_settings?.marker_color) {
      setSelectedColor(project.custom_fields.vfx_settings.marker_color)
    }
  }, [project])

  // Track changes
  const handleColorChange = (color: string) => {
    setSelectedColor(color)
    setHasChanges(true)

    // Track marker color selection
    track('vfx_marker_color_selected', {
      project_id: projectId,
      color: color,
    })
  }

  // Save settings
  const handleSave = async () => {
    try {
      const currentCustomFields = project?.custom_fields || {}

      await updateProjectMutation.mutateAsync({
        id: projectId,
        custom_fields: {
          ...currentCustomFields,
          vfx_settings: {
            ...currentCustomFields.vfx_settings,
            marker_color: selectedColor,
          },
        },
      })

      // Track VFX settings saved
      track('vfx_settings_saved', {
        project_id: projectId,
        marker_color: selectedColor,
      })

      setHasChanges(false)
      toast.success('VFX settings saved successfully')
    } catch (error) {
      console.error('Failed to save VFX settings:', error)
      toast.error('Failed to save settings')
    }
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-8">Loading settings...</div>
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>VFX Shot Detection</CardTitle>
          <CardDescription>
            Configure how VFX shots are identified when importing OTIO timelines
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Marker Color Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium">VFX Marker Color</label>
            <p className="text-sm text-muted-foreground">
              Choose which marker color identifies VFX shots. Any marker with this color will become a VFX shot,
              using the marker's name as the shot name.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-4">
              {MARKER_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => canEdit && handleColorChange(color.value)}
                  disabled={!canEdit}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border-2 transition-all
                    ${selectedColor === color.value
                      ? 'border-foreground bg-secondary shadow-md'
                      : 'border-border hover:border-foreground/50 hover:bg-secondary/50'
                    }
                    ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div
                    className="w-8 h-8 rounded border border-border shadow-sm"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span className="font-medium text-sm">{color.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Example */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-2">How it works</h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>1. When you upload an OTIO timeline, markers with <span className="font-semibold" style={{ color: MARKER_COLORS.find(c => c.value === selectedColor)?.hex }}>{selectedColor.toLowerCase()}</span> color will be detected</p>
              <p>2. The marker's name (e.g., "Shot_010_0010" or "BGB_020_0010") becomes the VFX shot name</p>
              <p>3. VFX shots are created automatically with frame ranges from the marker</p>
            </div>
          </div>

          {/* Save Button */}
          {canEdit && (
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || updateProjectMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {updateProjectMutation.isPending ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
